/* eslint-disable no-unused-vars */
import { TextField } from "@mui/material";
import { Node } from "@iventis/domain-model/model/node";
import React, { useEffect, useMemo } from "react";
import { Header5 } from "@iventis/styles/src/components/texts";
import { Content } from "@iventis/translations/content/typed-content";
import { ScheduleStatus } from "@iventis/domain-model/model/scheduleStatus";
import { useMachine } from "@xstate/react";
import { LoadingComponent, FormWizardTemplate, Stages, ConfirmationDialogComponent } from "@iventis/components";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { isAdmin, canEdit } from "@iventis/utilities";
import { hasPermissionsChanged } from "@iventis/permissions/src/permission-utilitites";
import { DraftSelector, PublishedSelector, StyledStatusSelector } from "./create-schedule";
import { EditScheduleMachine, isScheduleEqual, PERM_MACHINE_ID } from "./edit-schedule.machine";
import { ScheduleEditPermissionsComponent } from "./edit-schedule-permissions-component";
import { DeleteLocalScheduleVersion, SelectRootScheduleVersionEvent, UpdateScheduleEdit } from "./schedule.machine.types";

type ScheduleMachineEvents = DeleteLocalScheduleVersion | SelectRootScheduleVersionEvent | UpdateScheduleEdit;

export const ScheduleEditModal: React.FC<{
    node: Node;
    close: () => void;
    sendToScheduleMachine?: (event: ScheduleMachineEvents | ScheduleMachineEvents[]) => void;
}> = ({ node, close, sendToScheduleMachine }) => {
    const translate = useIventisTranslate();

    const [state, send] = useMachine(EditScheduleMachine, {
        context: {
            node,
        },
        actions: {
            onSave: ({ latestSchedule }) => {
                sendToScheduleMachine({
                    type: "UPDATE_SCHEDULE_VERSION",
                    payload: latestSchedule,
                });
            },
            onDelete: (context) => {
                const { isTopLevelSchedule } = context;
                const deleteEvent: DeleteLocalScheduleVersion = { type: "DELETE_SCHEDULE_VERSION_LOCAL", payload: node.id };
                sendToScheduleMachine(
                    isTopLevelSchedule
                        ? [
                              deleteEvent,
                              { type: "SELECT_ROOT_SCHEDULE_VERSION", payload: null }, // If payload is null, it will select the first schedule in the remaining list
                          ]
                        : deleteEvent
                );
            },
        },
    });

    const deleteConfirmationText = useMemo(() => {
        const { isTopLevelSchedule } = state.context;
        const options = {
            scheduleVersionName: node?.name,
        };
        const title = isTopLevelSchedule ? Content.schedule2.delete_schedule.title : Content.schedule2.delete_schedule_version.title;
        const message = isTopLevelSchedule ? Content.schedule2.delete_schedule.message : Content.schedule2.delete_schedule_version.message;
        return {
            title: translate(title),
            message: translate(message, options),
            confirm: translate(Content.common.buttons.confirm),
            cancel: translate(Content.common.buttons.cancel),
        };
    }, [translate, state.context.isTopLevelSchedule]);

    const loading = state.matches("loading") || state.context.permissions.latestPermissions == null; // Loading the edit schedule now relies on permissions becuase changing the status interacts with permission locks
    const idle = state.matches("idle");
    const validState = state.matches("idle.validation.valid");
    const deleting = state.matches("idle.deleteConfirmation.deleting");

    useEffect(() => {
        if (state.done) close();
    }, [state.value]);

    const EditStages: Stages = [
        {
            primaryButtonText: translate(Content.common.buttons.save),
            primaryButtonCallback: () => send("SAVE"),
            secondaryButtons: [{ buttonText: translate(Content.common.buttons.cancel), onButtonPressed: () => send("CLOSE") }],
            tertiaryButtonText: translate(Content.common.buttons.delete),
            tertiaryButtonDisabled: !isAdmin(node?.authorisation) || loading,
            tertiaryButtonCallback: () => send("DELETE"),
            isValid:
                (!isScheduleEqual(state.context.originalSchedule, state.context.latestSchedule) ||
                    hasPermissionsChanged(state.context.permissions.originalPermissions, state.context.permissions.latestPermissions)) &&
                validState,
        },
    ];
    return (
        <FormWizardTemplate title={translate(Content.schedule.edit_schedule_form.edit_schedule)} stages={EditStages} currentStage={0} isSubmitting={state.matches("saving")}>
            {loading && <LoadingComponent />}
            {!loading && (
                <>
                    <Header5 className="input-label space-within-content">{translate(Content.schedule.edit_schedule_form.name_field_label)}</Header5>
                    <TextField
                        key="name-field"
                        required
                        className="input-box space-between-content"
                        variant="outlined"
                        value={state.context.latestSchedule.name}
                        onChange={(event) => send({ type: "UPDATE_NAME", payload: event.target.value })}
                        disabled={!isAdmin(node?.authorisation)}
                    />
                    {state.context.isTopLevelSchedule && (
                        <>
                            <Header5 className="input-label space-within-content">{translate(Content.schedule.edit_schedule_form.status_field_label)}</Header5>
                            <div className="space-between-content">
                                <StyledStatusSelector
                                    options={[
                                        { id: "Draft", name: "Draft", component: DraftSelector },
                                        { id: "Published", name: "Published", component: PublishedSelector },
                                    ]}
                                    value={idle ? state.context.latestSchedule.status : ScheduleStatus.Draft}
                                    verticalPadding={12}
                                    changeValue={(output: string) => {
                                        send({ type: "UPDATE_STATUS", payload: ScheduleStatus[output] });
                                    }}
                                    disabled={!canEdit(node?.authorisation)}
                                    className="status"
                                />
                            </div>
                        </>
                    )}
                    {state.children[PERM_MACHINE_ID] && <ScheduleEditPermissionsComponent parentState={state} />}
                </>
            )}
            <ConfirmationDialogComponent
                dataCy="delete-schedule-confirmation-button"
                show={state.matches("idle.deleteConfirmation.open") || deleting}
                title={deleteConfirmationText.title}
                message={deleteConfirmationText.message}
                confirmText={deleteConfirmationText.confirm}
                cancelText={deleteConfirmationText.cancel}
                handleCancel={() => {
                    send("CANCEL_DELETE");
                }}
                handleConfirm={() => {
                    send("CONFIRM_DELETE");
                }}
                isSubmitting={deleting}
            />
        </FormWizardTemplate>
    );
};
